// LT-534 — "Add to campaign" bulk action from the Contacts page.
//
// Given a set of selected contacts, the operator either creates a new draft
// campaign with them as the audience, or adds them to an existing draft
// campaign. Under the hood the selection is materialised as a static
// ContactList (Approach A) and wired to the campaign's contact_list_id —
// reusing the launch-time snapshot path with no backend change.
//
// Audience edits are only legal while a campaign is in `draft` (mirrors the
// backend editable-field set), so the existing-campaign picker lists drafts
// only. For a draft that already points at a *static* list, the contacts are
// appended to that list; otherwise a fresh static list is created and set as
// the audience source (replacing any prior filter/list selection).

import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { campaignsAPI, extractCampaignApiError } from '../services/campaignsAPI';
import { contactListsAPI } from '../services/contactListsAPI';
import { createStaticListFromContacts, pickedListName } from '../utils/audienceList';
import type { Campaign } from '../types/campaign';
import { Button, IconButton, Input, Radio, Select } from '../ui';

export interface AddToCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactIds: string[];
  setToast: (t: { type: 'success' | 'error'; message: string }) => void;
  /** Called after a successful add so the caller can clear its selection. */
  onAdded?: () => void;
}

type Mode = 'new' | 'existing';

const AddToCampaignModal: React.FC<AddToCampaignModalProps> = ({
  isOpen,
  onClose,
  contactIds,
  setToast,
  onAdded,
}) => {
  const [mode, setMode] = useState<Mode>('new');
  const [newName, setNewName] = useState('');
  const [drafts, setDrafts] = useState<Campaign[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [targetCampaignId, setTargetCampaignId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = contactIds.length;

  useEffect(() => {
    if (!isOpen) return;
    setMode('new');
    setNewName('');
    setTargetCampaignId('');
    setError(null);
    setDraftsLoading(true);
    campaignsAPI
      .list({ status: ['draft'], limit: 200 })
      .then((rows) => setDrafts(rows))
      .catch(() => setDrafts([]))
      .finally(() => setDraftsLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onClose]);

  const handleConfirm = useCallback(async () => {
    if (count === 0) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const name = newName.trim();
        if (!name) {
          setError('Enter a campaign name.');
          setBusy(false);
          return;
        }
        const list = await createStaticListFromContacts(
          pickedListName(name, count),
          contactIds,
        );
        await campaignsAPI.create({ name, contact_list_id: list.id });
        setToast({
          type: 'success',
          message: `Created draft campaign "${name}" with ${count} contact${count === 1 ? '' : 's'}.`,
        });
      } else {
        if (!targetCampaignId) {
          setError('Pick a draft campaign.');
          setBusy(false);
          return;
        }
        const target = drafts.find((d) => d.id === targetCampaignId);
        const targetName = target?.name ?? 'campaign';
        // Append to an existing static list when the draft already has one;
        // otherwise create a fresh static list and set it as the audience.
        let existingStatic = false;
        if (target?.contact_list_id) {
          try {
            const cl = await contactListsAPI.get(target.contact_list_id);
            existingStatic = cl.list_type === 'static';
          } catch {
            existingStatic = false;
          }
        }
        if (existingStatic && target?.contact_list_id) {
          const res = await contactListsAPI.addMembers(target.contact_list_id, {
            contact_ids: contactIds,
          });
          setToast({
            type: 'success',
            message: `Added ${res.affected} new contact${res.affected === 1 ? '' : 's'} to "${targetName}".`,
          });
        } else {
          const list = await createStaticListFromContacts(
            pickedListName(targetName, count),
            contactIds,
          );
          await campaignsAPI.update(targetCampaignId, { contact_list_id: list.id });
          setToast({
            type: 'success',
            message: `Set ${count} contact${count === 1 ? '' : 's'} as the audience for "${targetName}".`,
          });
        }
      }
      onAdded?.();
      onClose();
    } catch (e) {
      setError(extractCampaignApiError(e, 'Failed to add contacts to campaign.'));
    } finally {
      setBusy(false);
    }
  }, [
    mode, newName, targetCampaignId, drafts, contactIds, count, setToast, onAdded, onClose,
  ]);

  if (!isOpen) return null;

  const targetIsReplacing =
    mode === 'existing'
    && targetCampaignId !== ''
    && (() => {
      const t = drafts.find((d) => d.id === targetCampaignId);
      return Boolean(t && (t.audience_filter || t.contact_list_id));
    })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-to-campaign-title"
    >
      <div
        className="w-full max-w-md bg-surface-1 border border-white/[0.16] rounded-ds-xl p-5 shadow-ds-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="add-to-campaign-title" className="text-lg font-semibold text-slate-50">
            Add {count} contact{count === 1 ? '' : 's'} to a campaign
          </h2>
          <IconButton
            intent="ghost"
            size="sm"
            aria-label="Close"
            onClick={() => !busy && onClose()}
            disabled={busy}
          >
            <X size={16} />
          </IconButton>
        </div>

        {error ? (
          <div className="mb-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-sm">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Radio
              name="add-to-campaign-mode"
              checked={mode === 'new'}
              onChange={() => setMode('new')}
              disabled={busy}
              label="Create a new draft campaign"
            />
            <Radio
              name="add-to-campaign-mode"
              checked={mode === 'existing'}
              onChange={() => setMode('existing')}
              disabled={busy}
              label="Add to an existing draft campaign"
            />
          </div>

          {mode === 'new' ? (
            <Input
              label="Campaign name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. NCR developers — Q3 outreach"
              disabled={busy}
              maxLength={255}
            />
          ) : (
            <div>
              <Select
                label="Draft campaign"
                value={targetCampaignId}
                onChange={(e) => setTargetCampaignId(e.target.value)}
                disabled={busy || draftsLoading}
              >
                <option value="">
                  {draftsLoading ? 'Loading…' : '— Select a draft —'}
                </option>
                {drafts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
              {!draftsLoading && drafts.length === 0 ? (
                <p className="text-xs text-slate-500 mt-1">
                  No draft campaigns. Create a new one instead.
                </p>
              ) : null}
              {targetIsReplacing ? (
                <p className="text-xs text-warn-text mt-1">
                  This draft already has an audience. The picked contacts will
                  be appended to its static list, or replace a filter/dynamic
                  audience with a new static list.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button intent="ghost" onClick={() => !busy && onClose()} disabled={busy}>
            Cancel
          </Button>
          <Button
            intent="primary"
            onClick={() => void handleConfirm()}
            disabled={busy || count === 0}
          >
            {busy ? 'Working…' : mode === 'new' ? 'Create campaign' : 'Add to campaign'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddToCampaignModal;
